/**
 * MeasurementDrawer - Create/Edit measurement drawer (large, 90%)
 * BuilderBel Construction Project Management System
 */

import React, { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { Input, TextArea, Dropdown, NumberInput } from '../forms';
import type {
  TakeoffMeasurement,
  CreateMeasurementRequest,
  MeasurementType,
  GeometryType,
} from '../../types/takeoff';

interface MeasurementDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: CreateMeasurementRequest) => void;
  isSubmitting?: boolean;
  editingMeasurement?: TakeoffMeasurement | null;
}

const schema = yup.object({
  pageNumber: yup.number().required('Page number is required').min(1).integer(),
  measurementType: yup
    .string()
    .required('Type is required')
    .oneOf(['linear', 'area', 'count', 'volume']),
  unit: yup.string().required('Unit is required'),
  costCode: yup.string(),
  costCodeDescription: yup.string(),
  wasteFactor: yup.number().min(1).max(3),
  label: yup.string().max(100),
  notes: yup.string().max(500),
  color: yup.string(),
  layerName: yup.string().max(50),
});

const measurementTypeOptions = [
  { value: 'linear', label: 'Linear' },
  { value: 'area', label: 'Area' },
  { value: 'count', label: 'Count' },
  { value: 'volume', label: 'Volume' },
];

const _geometryTypeOptions = [
  { value: 'line', label: 'Line' },
  { value: 'polyline', label: 'Polyline' },
  { value: 'polygon', label: 'Polygon' },
  { value: 'rectangle', label: 'Rectangle' },
  { value: 'circle', label: 'Circle' },
  { value: 'point', label: 'Point' },
];

const unitOptionsByType: Record<string, Array<{ value: string; label: string }>> = {
  linear: [
    { value: 'LF', label: 'Linear Feet (LF)' },
    { value: 'LM', label: 'Linear Meters (LM)' },
    { value: 'IN', label: 'Inches (IN)' },
    { value: 'CM', label: 'Centimeters (CM)' },
  ],
  area: [
    { value: 'SF', label: 'Square Feet (SF)' },
    { value: 'SY', label: 'Square Yards (SY)' },
    { value: 'SM', label: 'Square Meters (SM)' },
  ],
  count: [
    { value: 'EA', label: 'Each (EA)' },
    { value: 'PR', label: 'Pair (PR)' },
    { value: 'SET', label: 'Set (SET)' },
  ],
  volume: [
    { value: 'CF', label: 'Cubic Feet (CF)' },
    { value: 'CY', label: 'Cubic Yards (CY)' },
    { value: 'CM3', label: 'Cubic Meters (CM3)' },
  ],
};

const colorOptions = [
  { value: '#3B82F6', label: 'Blue' },
  { value: '#EF4444', label: 'Red' },
  { value: '#10B981', label: 'Green' },
  { value: '#F59E0B', label: 'Yellow' },
  { value: '#8B5CF6', label: 'Purple' },
  { value: '#F97316', label: 'Orange' },
  { value: '#06B6D4', label: 'Cyan' },
  { value: '#EC4899', label: 'Pink' },
];

const MeasurementDrawer: React.FC<MeasurementDrawerProps> = ({
  isOpen,
  onClose,
  onSubmit,
  isSubmitting = false,
  editingMeasurement,
}) => {
  const {
    register,
    handleSubmit,
    reset,
    setValue,
    watch,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(schema) as any,
    defaultValues: {
      pageNumber: 1,
      measurementType: 'linear' as string,
      unit: 'LF',
      costCode: '',
      costCodeDescription: '',
      wasteFactor: 1,
      label: '',
      notes: '',
      color: '#3B82F6',
      layerName: 'Default',
    },
  });

  const selectedType = watch('measurementType');
  const unitOptions = unitOptionsByType[selectedType] || unitOptionsByType.linear;

  useEffect(() => {
    if (editingMeasurement) {
      reset({
        pageNumber: editingMeasurement.pageNumber,
        measurementType: editingMeasurement.measurementType,
        unit: editingMeasurement.unit,
        costCode: editingMeasurement.costCode || '',
        costCodeDescription: editingMeasurement.costCodeDescription || '',
        wasteFactor: editingMeasurement.wasteFactor,
        label: editingMeasurement.label || '',
        notes: editingMeasurement.notes || '',
        color: editingMeasurement.color,
        layerName: editingMeasurement.layerName,
      });
    } else {
      reset({
        pageNumber: 1,
        measurementType: 'linear',
        unit: 'LF',
        costCode: '',
        costCodeDescription: '',
        wasteFactor: 1,
        label: '',
        notes: '',
        color: '#3B82F6',
        layerName: 'Default',
      });
    }
  }, [editingMeasurement, reset]);

  const handleFormSubmit = (formData: Record<string, unknown>) => {
    const data: CreateMeasurementRequest = {
      pageNumber: formData.pageNumber as number,
      measurementType: formData.measurementType as MeasurementType,
      geometry: editingMeasurement?.geometry || {
        type: 'point' as GeometryType,
        coordinates: [[0, 0]],
      },
      unit: formData.unit as string,
      costCode: formData.costCode as string,
      costCodeDescription: formData.costCodeDescription as string,
      wasteFactor: formData.wasteFactor as number,
      label: formData.label as string,
      notes: formData.notes as string,
      color: formData.color as string,
      layerName: formData.layerName as string,
    };
    onSubmit(data);
  };

  if (!isOpen) return null;

  return createPortal(
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/30 backdrop-blur-sm z-[90]"
        onClick={onClose}
      />

      {/* Drawer - Large (90% / inset-6) */}
      <div className="fixed inset-6 z-[100] animate-in slide-in-from-right">
        <div className="bg-white rounded-2xl shadow-2xl h-full flex flex-col">
          {/* Header */}
          <div className="flex items-center justify-between px-8 py-5 border-b">
            <h2 className="text-lg font-semibold text-gray-900">
              {editingMeasurement ? 'Edit Measurement' : 'New Measurement'}
            </h2>
            <button
              onClick={onClose}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <X className="w-5 h-5 text-gray-500" />
            </button>
          </div>

          {/* Content */}
          <form
            onSubmit={handleSubmit(handleFormSubmit)}
            className="flex-1 flex flex-col overflow-hidden"
          >
            <div className="flex-1 overflow-y-auto px-8 py-6">
              <div className="grid grid-cols-2 gap-8">
                {/* Left Column - Measurement Properties */}
                <div className="space-y-4">
                  <h3 className="text-sm font-medium text-gray-700 mb-3">
                    Measurement Properties
                  </h3>

                  <div className="grid grid-cols-2 gap-4">
                    <Dropdown
                      label="Measurement Type"
                      options={measurementTypeOptions}
                      value={selectedType}
                      onChange={val => {
                        setValue('measurementType', val);
                        const defaults = unitOptionsByType[val];
                        if (defaults?.[0]) setValue('unit', defaults[0].value);
                      }}
                      error={errors.measurementType?.message}
                    />
                    <Dropdown
                      label="Unit"
                      options={unitOptions}
                      value={watch('unit')}
                      onChange={val => setValue('unit', val)}
                      error={errors.unit?.message}
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <NumberInput
                      label="Page Number"
                      value={watch('pageNumber') || 1}
                      onChange={val => setValue('pageNumber', val)}
                      error={errors.pageNumber?.message}
                    />
                    <NumberInput
                      label="Waste Factor"
                      step={0.01}
                      value={watch('wasteFactor') || 1}
                      onChange={val => setValue('wasteFactor', val)}
                      error={errors.wasteFactor?.message}
                    />
                  </div>

                  <Input
                    label="Label"
                    placeholder="Optional label"
                    error={errors.label?.message}
                    {...register('label')}
                  />

                  <TextArea
                    label="Notes"
                    placeholder="Optional notes"
                    value={watch('notes') || ''}
                    onChange={val => setValue('notes', val)}
                    error={errors.notes?.message}
                  />
                </div>

                {/* Right Column - Cost Code & Display */}
                <div className="space-y-4">
                  <h3 className="text-sm font-medium text-gray-700 mb-3">
                    Cost Code & Display
                  </h3>

                  <Input
                    label="Cost Code"
                    placeholder="e.g. 03-100"
                    error={errors.costCode?.message}
                    {...register('costCode')}
                  />

                  <Input
                    label="Cost Code Description"
                    placeholder="e.g. Concrete Formwork"
                    error={errors.costCodeDescription?.message}
                    {...register('costCodeDescription')}
                  />

                  <Dropdown
                    label="Color"
                    options={colorOptions}
                    value={watch('color') || '#3B82F6'}
                    onChange={val => setValue('color', val)}
                  />

                  <Input
                    label="Layer Name"
                    placeholder="Default"
                    error={errors.layerName?.message}
                    {...register('layerName')}
                  />

                  {/* Drawing Viewer Placeholder */}
                  <div className="mt-6 p-6 bg-gray-50 rounded-lg border-2 border-dashed border-gray-200 text-center">
                    <p className="text-sm text-gray-500">
                      Drawing Viewer — Coming Soon
                    </p>
                    <p className="text-xs text-gray-400 mt-1">
                      Click-to-measure on drawings will be available in a future
                      update
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className="border-t px-8 py-6 bg-gray-50 rounded-b-2xl flex justify-end space-x-3">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 text-sm font-medium text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={isSubmitting}
                className="px-4 py-2 text-sm font-medium text-white bg-brand-600 rounded-lg hover:bg-brand-700 disabled:bg-brand-200 transition-colors"
              >
                {isSubmitting
                  ? 'Saving...'
                  : editingMeasurement
                    ? 'Update Measurement'
                    : 'Add Measurement'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </>,
    document.body
  );
};

export default MeasurementDrawer;
